import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, X, Download } from 'lucide-react';

/* PwaInstallPrompt — shows a subtle bottom-sheet on mobile when the browser
 * fires `beforeinstallprompt`. Dismissible, remembers decision. */
const DISMISS_KEY = 'zv_pwa_dismiss_v1';

export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - Number(dismissed) < 1000 * 60 * 60 * 24 * 7) return; // 7d

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setVisible(false);
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-[380px] z-[80]"
          data-testid="pwa-install-prompt"
        >
          <div className="rounded-2xl bg-white border border-[#E4E4E7] shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18)] p-4 sm:p-5 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#00D387] to-[#0F8A5F] flex items-center justify-center shrink-0">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[14px] text-primary leading-tight">Zertivo als App installieren</p>
              <p className="text-[12.5px] text-secondary mt-1 leading-[1.5]">
                Schneller Zugriff, offline nutzbar, Push-Benachrichtigungen.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={install}
                  data-testid="pwa-install-accept"
                  className="h-9 px-4 rounded-lg bg-gradient-to-r from-[#00D387] to-[#0F8A5F] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:scale-[1.02] active:scale-[0.98] transition-transform"
                >
                  <Download className="w-3.5 h-3.5" /> Installieren
                </button>
                <button
                  onClick={dismiss}
                  data-testid="pwa-install-dismiss"
                  className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-secondary hover:bg-[#F4F4F5] transition-colors"
                >
                  Später
                </button>
              </div>
            </div>
            <button onClick={dismiss} aria-label="Schließen"
                    className="w-7 h-7 rounded-lg hover:bg-[#F4F4F5] flex items-center justify-center text-[#A1A1AA] shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
